import { useEffect, useRef, useState } from "react";
import { cjsDefault } from "../../lib/cjs-default";
import reactFadeIn from "react-fade-in";
import { useHistory, useLocation } from "react-router-dom";
import { displayToast } from "../../components/app/AppToast";
import ContentHeader from "../../components/app/headers/ContentHeader";
import BookMarkSimpleIcon from "../../assets/icons/bookmark-simple.svg?react";
import BookMarkFilledIcon from "../../assets/icons/bookmark-filled.svg?react";
import ShareV2Icon from "../../assets/icons/share-v2.svg?react";
import ArticlePublicationCard from "../../components/cards/ArticlePublicationCard";

import VideoCard from "../../components/cards/VideoCard";
import ShareSheet from "../../components/SharedBySheet";
import { useCurrentUser } from "../../domains/user/hooks/useCurrentUser";
import { useEffectOnce } from "../../domains/app/hooks/useEffectOnce";
import { usePageScroll } from "../../domains/app/hooks/usePageScroll";
import { DebugLogger } from "../../domains/app/utils/debugLogger";
import { handleErrorContentNotFound } from "../../domains/app/utils/errorHandler";
import { useSavePlaylist } from "../../domains/playlist/hooks/useSavePlaylist";
import { useClaimSharedPlaylist } from "../../domains/playlist/hooks/useClaimSharedPlaylist";
import { playlistScrollStore } from "../../domains/playlist/stores/playlistScrollStore";
import { useApiQuery } from "../../services/api";
import { sharedContentBackDestination } from "../../domains/room/utils/roomContentPath";
import { goBackInApp } from "../../domains/app/utils/backNavigation";
import { useSharePlaylist } from "../../domains/playlist/hooks/useSharePlaylist";
import {
  PLAYLIST_ITEM_ATTRIBUTE,
  usePlaylistProgress,
} from "./usePlaylistProgress";
import { AnalyticsItemList } from "../../tools/analytics/analytics.types";
import { iosSetBackgroundColor, isIOSApp } from "../../tools/ios";
import ScrollableLayout from "../../components/layout/ScrollableLayout";
import { useTranslation } from "react-i18next";
import PlaylistPageHeader from "./PlaylistPageHeader";
import { PlaylistComplianceNotes } from "./PlaylistComplianceNotes";
import {
  PlaylistPageHeaderSkeleton,
  PlaylistRowsSkeleton,
} from "../../components/skeleton/PlaylistPageSkeleton";
import { useHeroImageIsDark } from "../../domains/app/hooks/useHeroImageIsDark";
import { usePlaylistPageView } from "../../domains/playlist/hooks/usePlaylistPageView";
import { isDesktopLayout } from "../../common/hooks/useIsDesktopView";
import { DesktopPlaylistPage } from "./desktop/DesktopPlaylistPage";

const FadeIn = cjsDefault(reactFadeIn);

const debug = new DebugLogger("Public Playlist Page");
debug.mute();

export default function PlaylistPage(props: {
  id: string;
  roomId: string | null;
}) {
  if (isDesktopLayout()) {
    // Keyed by the collection: opening another one from the rail starts at
    // its top, with its own banner state, instead of the previous scroll.
    return (
      <DesktopPlaylistPage key={props.id} id={props.id} roomId={props.roomId} />
    );
  }
  return <MobilePlaylistPage {...props} />;
}

function MobilePlaylistPage({
  id,
  roomId,
}: {
  id: string;
  roomId: string | null;
}) {
  const history = useHistory();
  const location = useLocation();
  const { t } = useTranslation();
  const { currentUser, isAuthenticated } = useCurrentUser();
  const playlistId = id;
  useClaimSharedPlaylist(playlistId);
  const playlistQuery = useApiQuery(
    "/webapp/public/playlists/:playlistId",
    playlistId === "" ? false : { param: { playlistId } }
  );
  const roomQuery = useApiQuery(
    "/webapp/private/rooms/:id",
    roomId !== null ? { param: { id: roomId } } : false
  );
  const currentRoom = roomQuery.state === "success" ? roomQuery.body : null;
  const playlist =
    playlistQuery.state === "success" ? playlistQuery.body : undefined;
  const isPlaylistLoading =
    playlistQuery.state === "pending" && playlistQuery.isFetching;
  const articleIds = [...new Set(playlist?.playlist)];
  const contentIds = [...new Set(playlist?.playlistContent)];
  // Known from the (possibly seeded) playlist before the content batch resolves,
  // so the header count and the skeleton row count are correct immediately.
  const itemCount = articleIds.length + contentIds.length;
  const batchQuery = useApiQuery(
    "/webapp/public/content/batch-mixed",
    articleIds.length === 0 && contentIds.length === 0
      ? false
      : {
          query: {
            articleIds: articleIds.join(","),
            contentIds: contentIds.join(","),
          },
        }
  );
  const playlistContent =
    batchQuery.state === "success"
      ? [...batchQuery.body.articles, ...batchQuery.body.contents]
      : undefined;
  const isContentLoading = batchQuery.state === "pending";
  const isLoading = isPlaylistLoading || isContentLoading;
  const hadCachedPlaylist = useRef(!isLoading && playlist !== undefined);
  const { savePageScrollY, pageScrollY } = usePageScroll();
  const pageScrollYRef = useRef(pageScrollY);
  const savePageScrollYRef = useRef(savePageScrollY);
  pageScrollYRef.current = pageScrollY;
  savePageScrollYRef.current = savePageScrollY;
  const scrollRef = useRef<HTMLDivElement>(null);
  const [isScrolled, setScrolled] = useState(false);
  const firstHeroImage =
    playlist === undefined ? undefined : playlist.images[0];
  const heroImageUrl = firstHeroImage === undefined ? null : firstHeroImage.url;
  const heroIsDark = useHeroImageIsDark(heroImageUrl);
  // Over the blurred hero art the header is transparent — flip its content to a
  // light tone on dark art. Once scrolled onto the white body it's always dark.
  const headerContentColor =
    !isScrolled && heroIsDark === true ? "light" : "dark";
  const { triggerShare: sharePlaylist } = useSharePlaylist(
    roomId ?? null,
    playlist ?? null,
    AnalyticsItemList.PLAYLIST_PAGE
  );
  const { isSaved, toggleSave } = useSavePlaylist(
    playlist ?? null,
    AnalyticsItemList.PLAYLIST_PAGE
  );

  const items = playlistContent ?? [];
  const playlistProgress = usePlaylistProgress(
    playlist === undefined || isContentLoading ? null : playlistId
  );

  const ownerUserId = playlist === undefined ? null : playlist.userId;
  const sharedWithUserIds =
    playlist === undefined ? [] : playlist.sharedWithUserIds;
  const isMslDirectShare = sharedWithUserIds.length > 0;
  const isOwnerOrRecipient =
    currentUser !== null &&
    (currentUser.id === ownerUserId ||
      sharedWithUserIds.includes(currentUser.id));
  // A playlist shared with you already lives in "My shared playlists" — saving
  // (bookmarking) it on top is redundant, so the save action is hidden.
  const isSharedWithMe =
    currentUser !== null && sharedWithUserIds.includes(currentUser.id);
  const canSave = !isSharedWithMe;
  const canShare =
    currentUser !== null &&
    playlist !== undefined &&
    (isMslDirectShare ? isOwnerOrRecipient : true);

  useEffectOnce(() => {
    initialize();
  }, []);

  usePlaylistPageView({
    playlist: playlist === undefined ? null : playlist,
    roomId,
    currentRoom,
    isRoomSettled: roomQuery.state !== "pending",
    userId: currentUser === null ? null : currentUser.id,
  });

  // Restore scroll position when navigating back, but only for the same playlist
  useEffect(() => {
    const savedScrollPlaylistId =
      playlistScrollStore.get().lastDetailPlaylistId;

    if (!isLoading && scrollRef.current) {
      if (
        savedScrollPlaylistId === playlistId &&
        pageScrollYRef.current > 0 &&
        playlist !== undefined
      ) {
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            scrollRef.current?.scrollTo({
              top: pageScrollYRef.current,
              behavior: "instant",
            });
          });
        });
      } else if (savedScrollPlaylistId !== playlistId) {
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            scrollRef.current?.scrollTo({ top: 0, behavior: "instant" });
            savePageScrollYRef.current(0);
          });
        });
      }
    }
  }, [isLoading, location.key, playlistId, playlist]);

  function initialize() {
    try {
      debug.log("Initializing page");

      if (isIOSApp()) iosSetBackgroundColor("#FBF9F5");
    } catch (error) {
      if (handleErrorContentNotFound(error)) return;
      console.error("Couldn't mount public playlist page.", error);

      displayToast(t("Something went wrong. Please try again."), "error");
      history.replace("/discovery");
    }
  }

  return (
    <ScrollableLayout
      scrollRef={scrollRef}
      headerBackground={isScrolled ? "rgba(251, 249, 245, 0.7)" : "transparent"}
      headerStyle={{
        backdropFilter: isScrolled ? "blur(12px)" : "none",
        WebkitBackdropFilter: isScrolled ? "blur(12px)" : "none",
        transition: "background 0.2s ease, backdrop-filter 0.2s ease",
      }}
      onScroll={(e) => {
        const scrollTop = e.currentTarget.scrollTop;
        setScrolled(scrollTop > 20);
      }}
      header={
        <ContentHeader
          transparent
          contentColor={headerContentColor}
          // Over the hero art: no divider line. The border returns once scrolled
          // onto the white body, matching every other page.
          className={isScrolled ? "" : "border-b-0"}
          backLabel={t("Back")}
          onBack={() => {
            playlistProgress.noteExit("back");
            goBackInApp(
              history,
              sharedContentBackDestination(
                isAuthenticated
                  ? {
                      kind: "signed-in",
                      roomId,
                      hasRoom: roomQuery.state === "success",
                    }
                  : { kind: "signed-out" }
              )
            );
          }}
          actions={
            playlist === undefined ? null : (
              <>
                {canSave &&
                  (isSaved ? (
                    <BookMarkFilledIcon
                      className="cursor-pointer"
                      onClick={toggleSave}
                    />
                  ) : (
                    <BookMarkSimpleIcon
                      className="cursor-pointer"
                      onClick={toggleSave}
                    />
                  ))}
                {canShare && (
                  <ShareV2Icon
                    className="cursor-pointer"
                    onClick={() => {
                      playlistProgress.noteExit("share");
                      void sharePlaylist();
                    }}
                  />
                )}
              </>
            )
          }
        />
      }
      style={{
        paddingTop: "0px",
        backgroundColor: "var(--color-ds-white)",
      }}
    >
      <div className="mx-auto w-full md:max-w-[760px] md:px-10">
        {playlist === undefined ? (
          <PlaylistPageHeaderSkeleton />
        ) : (
          <PlaylistPageHeader
            playlist={playlist}
            // While the content batch loads, count the playlist's content ids
            // (instant from the seed); once it resolves, use the actual rendered
            // items so the header reconciles with what's shown.
            articleCount={isContentLoading ? itemCount : items.length}
            t={t}
            desktopActions={
              <div className="hidden items-center justify-end gap-1 md:flex">
                {canSave && (
                  <button
                    type="button"
                    onClick={toggleSave}
                    title={isSaved ? t("Saved") : t("Save")}
                    className="inline-flex size-9 cursor-pointer items-center justify-center rounded-full transition-colors hover:bg-ds-black/4"
                  >
                    {isSaved ? (
                      <BookMarkFilledIcon className="size-4.5" />
                    ) : (
                      <BookMarkSimpleIcon className="size-4.5" />
                    )}
                  </button>
                )}
                {canShare && (
                  <button
                    type="button"
                    onClick={() => {
                      playlistProgress.noteExit("share");
                      void sharePlaylist();
                    }}
                    title={t("Share")}
                    className="inline-flex size-9 cursor-pointer items-center justify-center rounded-full transition-colors hover:bg-ds-black/4"
                  >
                    <ShareV2Icon className="size-4.5" />
                  </button>
                )}
              </div>
            }
          />
        )}
        {(() => {
          // Header paints instantly from the seeded playlist; the article/video
          // rows arrive with the content batch, so skeleton them meanwhile. The
          // count comes from the playlist's content ids (a small default while
          // the playlist itself is still loading).
          if (playlist === undefined || isContentLoading) {
            return (
              <PlaylistRowsSkeleton count={itemCount > 0 ? itemCount : 6} />
            );
          }
          const list = (
            <div
              ref={playlistProgress.listRef}
              className="flex w-full flex-col divide-y divide-ds-slate-100 px-4 pt-4 *:py-4"
              style={{ paddingBottom: "calc(16px + var(--safe-area-bottom))" }}
            >
              {items.map((item) => {
                const onCardClick = () => {
                  playlistProgress.noteItemOpened(item.id);
                  savePageScrollY(scrollRef.current?.scrollTop ?? 0);
                  playlistScrollStore.set((prev) => ({
                    ...prev,
                    lastDetailPlaylistId: playlistId,
                  }));
                };
                switch (item.content_format) {
                  case "Article": {
                    return (
                      <div
                        key={item.id}
                        {...{ [PLAYLIST_ITEM_ATTRIBUTE]: item.id }}
                      >
                        <ArticlePublicationCard
                          roomId={roomId ?? null}
                          article={item}
                          analyticsListName={AnalyticsItemList.PLAYLIST_PAGE}
                          recommendation={null}
                          onClick={onCardClick}
                          className="bg-transparent"
                          showImage={true}
                        />
                      </div>
                    );
                  }
                  case "Nectar": {
                    return null;
                  }
                  case "Video": {
                    return (
                      <div
                        key={item.id}
                        {...{ [PLAYLIST_ITEM_ATTRIBUTE]: item.id }}
                      >
                        <VideoCard
                          roomId={roomId ?? null}
                          video={item}
                          analyticsListName={AnalyticsItemList.PLAYLIST_PAGE}
                          recommendation={null}
                          onClick={onCardClick}
                          className={null}
                        />
                      </div>
                    );
                  }
                  case "Infographic":
                  case "Audio": {
                    return null;
                  }
                  default: {
                    throw new Error(item satisfies never);
                  }
                }
              })}
              <PlaylistComplianceNotes playlist={playlist} className={null} />
            </div>
          );
          return hadCachedPlaylist.current ? list : <FadeIn>{list}</FadeIn>;
        })()}
      </div>
      <ShareSheet type="playlist" />
    </ScrollableLayout>
  );
}
